import { getCachedUser } from '../hooks/useAuth';
/**
 * ProfilePhotoEditor.jsx - Upload e editor foto profilo
 * Include cropping e resize dell'immagine
 */

import React, { useState, useRef, useCallback } from 'react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Camera, Upload, X, Check, Loader2, Trash2, RotateCw } from 'lucide-react';
import { toast } from 'sonner';

const ProfilePhotoEditor = ({ currentPhoto, onPhotoChange }) => {
  const [showEditor, setShowEditor] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ unit: '%', width: 80, aspect: 1 });
  const [completedCrop, setCompletedCrop] = useState(null);
  const imgRef = useRef(null);
  const fileInputRef = useRef(null);

  const onSelectFile = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setImageSrc(reader.result);
        setShowEditor(true);
      });
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const onImageLoad = useCallback((e) => {
    imgRef.current = e.currentTarget;
    
    // Center the crop
    const { width, height } = e.currentTarget;
    const size = Math.min(width, height) * 0.8;
    const x = (width - size) / 2;
    const y = (height - size) / 2;
    
    setCrop({
      unit: 'px',
      width: size,
      height: size,
      x,
      y
    });
  }, []);

  const getCroppedImg = useCallback(() => {
    if (!completedCrop || !imgRef.current) return null;

    const canvas = document.createElement('canvas');
    const scaleX = imgRef.current.naturalWidth / imgRef.current.width;
    const scaleY = imgRef.current.naturalHeight / imgRef.current.height;
    
    // Output size: 256x256 for profile photos
    const outputSize = 256;
    canvas.width = outputSize;
    canvas.height = outputSize;
    
    const ctx = canvas.getContext('2d');
    
    ctx.drawImage(
      imgRef.current,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      outputSize,
      outputSize
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob);
      }, 'image/jpeg', 0.9);
    });
  }, [completedCrop]);

  const handleSave = async () => {
    setUploading(true);
    
    try {
      const croppedBlob = await getCroppedImg();
      if (!croppedBlob) {
        toast.error('Errore nel ritaglio dell\'immagine');
        return;
      }

      // Create form data
      const formData = new FormData();
      formData.append('file', croppedBlob, 'profile.jpg');
      formData.append('type', 'profile');

      // Upload to media endpoint
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/media/upload`, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const data = await response.json();
      
      // Update profile with new URL
      onPhotoChange(data.url);
      
      // Also update in Supabase profiles table
      const { default: supabase } = await import('../lib/supabase');
      const user = getCachedUser();
      
      if (user) {
        await supabase
          .from('profiles')
          .update({ avatar_url: data.url })
          .eq('id', user.id);
      }

      toast.success('Foto profilo aggiornata');
      setShowEditor(false);
      setImageSrc(null);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Errore nel caricamento della foto');
    } finally {
      setUploading(false);
    }
  };

  const handleRemovePhoto = async () => {
    if (!window.confirm('Rimuovere la foto profilo?')) return;
    
    try {
      const { default: supabase } = await import('../lib/supabase');
      const user = getCachedUser();
      
      if (user) {
        await supabase
          .from('profiles')
          .update({ avatar_url: null })
          .eq('id', user.id);
      }

      onPhotoChange(null);
      toast.success('Foto profilo rimossa');
    } catch (error) {
      console.error('Remove error:', error);
      toast.error('Errore nella rimozione');
    }
  };

  const cancelEdit = () => {
    setShowEditor(false);
    setImageSrc(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="pb-6 border-b border-white/10">
      <label className="block text-sm font-medium text-white/70 mb-3">
        Foto Profilo
      </label>
      
      <div className="flex items-center gap-6">
        {/* Current Photo / Placeholder */}
        <div className="relative group">
          <div className="w-24 h-24 rounded-full overflow-hidden bg-white/5 border-2 border-white/10">
            {currentPhoto ? (
              <img 
                src={currentPhoto} 
                alt="Profile" 
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Camera className="w-8 h-8 text-white/20" />
              </div>
            )}
          </div>
          
          {/* Overlay on hover */}
          <div 
            className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center gap-2 transition-opacity cursor-pointer"
            onClick={() => fileInputRef.current?.click()}
          >
            <Camera className="w-5 h-5 text-white" />
          </div>
        </div>
        
        {/* Upload Button */}
        <div className="flex flex-col gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={onSelectFile}
            className="hidden"
          />
          
          <button
            onClick={() => fileInputRef.current?.click()}
            className="flex items-center gap-2 px-4 py-2 bg-[#B08B5C] text-black rounded-lg text-sm font-medium hover:bg-[#C9A46E] transition-colors"
          >
            <Upload className="w-4 h-4" />
            Carica foto
          </button>
          
          {currentPhoto && (
            <button
              onClick={handleRemovePhoto}
              className="flex items-center gap-2 px-4 py-2 border border-white/10 text-white/60 rounded-lg text-sm hover:bg-white/5 hover:text-red-400 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              Rimuovi
            </button>
          )}
          
          <p className="text-xs text-white/40">
            JPG, PNG o WebP. Max 5MB.
          </p>
        </div>
      </div>

      {/* Editor Modal */}
      {showEditor && imageSrc && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
          <div className="bg-[#1a1a18] rounded-xl border border-white/10 p-6 max-w-lg w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-white">Ritaglia foto</h3>
              <button
                onClick={cancelEdit}
                className="p-2 text-white/40 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="bg-black rounded-lg overflow-hidden mb-4">
              <ReactCrop
                crop={crop}
                onChange={(_, percentCrop) => setCrop(percentCrop)}
                onComplete={(c) => setCompletedCrop(c)}
                aspect={1}
                circularCrop
              >
                <img
                  src={imageSrc}
                  onLoad={onImageLoad}
                  alt="Crop preview"
                  style={{ maxHeight: '400px', width: '100%', objectFit: 'contain' }}
                />
              </ReactCrop>
            </div>
            
            <p className="text-xs text-white/40 mb-4 text-center">
              Trascina per riposizionare, usa gli angoli per ridimensionare
            </p>
            
            <div className="flex justify-end gap-3">
              <button
                onClick={cancelEdit}
                className="px-4 py-2 text-white/60 hover:text-white"
              >
                Annulla
              </button>
              <button
                onClick={handleSave}
                disabled={uploading || !completedCrop}
                className="flex items-center gap-2 px-4 py-2 bg-[#B08B5C] text-black rounded-lg font-medium hover:bg-[#C9A46E] disabled:opacity-50"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Caricamento...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Salva foto
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePhotoEditor;
